import type { TaskDecisionPrompt } from '@wix/ai-scheduling-assistant-common-lib/utils';
import { bucketOfTask, orderedDistinctAgentIds, sortedForTaskList, workingCounts, type BucketableTask } from './agent-task-buckets';
import { hasOpenDecision } from './task-schedule';
import { groupWaitingCards } from './waiting-cards';
import { interleaveByRecency } from './interleave-by-recency';
import { actionNeededCount, outreachOnlyQuestions } from './action-needed-count';
import type { OpenAsksRead } from './open-asks';
import { contactDisplayName, threadChannel, threadTitle } from './contact-rows';
import { threadByOutreachId, type StageEntry, type StageThread } from './outreach-stage';

// The shared view-model for the island quick-view (the pill-anchored bubble on every surface). Pure and
// mirrored 1:1 on native so all three show the same rows in the same order.
//
// **The two tones no longer open the same rows, and neither one is an all-status peek.** Each card lists
// exactly what its own pill claims:
//   • BLUE  — "{n} agents at work": the tasks with a VERIFIED live run (`buildWorkingRows`).
//   • ORANGE — "{n} Action needed": the tasks in the WAITING bucket and nothing else (`rows` below).
// That was not always so. The orange card used to render an all-status peek, which is what produced the
// reported screen: a header reading "1 Action needed" above one IN_PROGRESS row, one ACTION_NEEDED row and
// a folded "2 Done" row. A card whose header states a number and whose body lists three other things makes
// the reader do arithmetic that cannot come out.
//
// **The rows are still a PROJECTION of the account-wide Tasks list, not a second list** — the same set the
// Tasks screen renders (outreach rows included), the same recency window, the same reading order
// (`sortedForTaskList`: unfinished above finished, newest-updated within each half) and the same origin
// fold. Only the POPULATION narrows, and it narrows at this builder's own input: the shared order/window/
// fold rules in `agent-task-buckets` are used, never modified, so the Tasks screen — which composes those
// same rules directly — cannot be touched by anything here. This builder feeds the island card and nothing
// else (verified: no other caller), which is why the filter can live in it at all.
//
// Capped to a peek with an overflow count and a "see all" footer. Each row carries enough to read at a
// glance — status, title, the Space it lives in, its agent, and a short summary. The row mapping happens
// at the call site.

export interface QuickViewTask extends BucketableTask {
  id: string;
  title: string;
  /** Required here (widened from BucketableTask's optional) so the row's StatusBadge always has a value. */
  status: string;
  agentId?: string;
  workspaceId: string;
  workspaceName: string;
  /** A short "what it's about" line, when the task carries one. */
  summary?: string;
  /** The routine (pulse identity) whose firing created this task; absent for one-off work. Carried so
   *  the peek can fold consecutive runs of one routine, exactly as the Tasks screen does. */
  pulseIdentityId?: string;
  /** The goal whose plan laid this task out; absent on work that serves no goal. Carried for the same
   *  reason as `pulseIdentityId`: the peek folds BOTH origins, at the one shared threshold, or a plan's
   *  tasks read as one folded line on the Tasks screen and as several loose rows in the bubble. */
  goalId?: string;
  /** The agent's own present-continuous status line — the working row's hero sentence when present.
   *  Empty string means absent (the mapping normalizes it away). */
  currentAction?: string;
  /** The Master Plan steps, for the sentence chain's second tier: the first NOT-completed step title. */
  plan?: { title: string; completed: boolean }[];
  /** Newest write on the task's own conversations, ISO — the working rows' sort key and elapsed stamp.
   *  Absent when the list did not compute run state for this row. */
  lastActivityAt?: string;
  /** Widened from `BucketableTask`'s structural minimum (`{decisionId}[]`) to the full prompt, so the
   *  same `TaskDecisionPrompts` block that answers the list row can answer the peek row too — one
   *  implementation, not a second one shaped for this surface's own narrower type. */
  decisionPrompts?: TaskDecisionPrompt[];
}

/**
 * One open question the orange card stands for, and every waiting task that named it (`tasks.length >= 1`,
 * in the list's own reading order). Present on a row ONLY when that row is a question CARD — the island's
 * one-question-one-card collapse (`groupWaitingCards`).
 *
 * `prompt` is the FIRST occurrence's prompt and the only one the card ever answers: the verdict echoes
 * `planVersion` verbatim, so the rendered prompt is the one the hold is taken from.
 *
 * It carries the task TITLES and nothing more. The card may not say what an answer will release — a
 * goal-scoped settle releases the matching branch and retires the others, and the branch a task belongs to
 * (`DecisionBlocker.requiredChoice`) never reaches the client — so the list sits under "Waiting on this
 * answer" and makes no claim beyond that.
 */
export interface QuickViewQuestion {
  decisionId: string;
  prompt: TaskDecisionPrompt;
  tasks: QuickViewTask[];
}

/**
 * One rendered CARD of the peek: either a waiting task on its own, or ONE QUESTION standing for every task
 * blocked on it (`question` present — see {@link QuickViewQuestion}). A question card's own fields are the
 * FIRST of those tasks': the agent, the Space and the id a tap opens and BI reports.
 *
 * `runCount` is 1 on every card, including a question card that stands for three tasks: the orange card's
 * arithmetic counts CARDS (`shown + moreCount === waitingCount`), and the tasks a card stands for are
 * named in its own body rather than hidden behind it. (It is NOT the origin fold — the redesign dropped
 * that from this card; the field stays because the Tasks screen's `TaskOriginGroup` shape still flows
 * through here and `onOpen` branches on it.)
 */
export interface QuickViewRow extends QuickViewTask {
  runCount: number;
  /** Set when this row is a question card rather than a plain waiting task. */
  question?: QuickViewQuestion;
  /**
   * Set on an OUTREACH-ONLY question card (phase 5, island 3a): a question raised on an outreach thread with
   * no task, goal or meeting behind it, so no waiting task carries it. Such a row has `question` with
   * `tasks: []` and no task of its own — `id` is `''`, there is no Space and no agent (the Maestro's). It
   * opens its CONVERSATION, never a task. See {@link islandCardId} for its identity.
   */
  outreachOnly?: true;
}

/**
 * An outreach-only open question as `/api/tasks` serves it (`outreachQuestions`): the decision prompt, the
 * thread it was raised on, and when. Structural (common-lib `OutreachQuestionPrompt`) so this module stays
 * importable from a plain unit test.
 */
export type { IslandOutreachQuestion } from './action-needed-count';
import type { IslandOutreachQuestion } from './action-needed-count';

/**
 * A card's identity — what the React key, and the shared contract's `cardIds`, are spelled in:
 * `task:<taskId>` (a waiting task with no groupable question), `question:<decisionId>` (a task-borne question
 * card), `outreach:<decisionId>` (an outreach-only card). The SAME spelling iOS and Android map their cards to
 * in their own runs of `contract.json`'s `island.cases`, so the three surfaces are compared on one vocabulary.
 */
export function islandCardId(row: QuickViewRow): string {
  if (row.outreachOnly && row.question) return `outreach:${row.question.decisionId}`;
  if (row.question) return `question:${row.question.decisionId}`;
  return `task:${row.id}`;
}

export interface IslandQuickView {
  /** The ORANGE card's CARDS — the ACTION-REQUIRED rows (the `waiting` bucket) in the Tasks list's reading
   *  order, with each open QUESTION collapsed to one card over every task blocked on it
   *  (`groupWaitingCards`), capped to `QUICK_VIEW_SHOWN`. Nothing else appears here: the card's header
   *  counts the things waiting on the reader, so its body may only contain those things. */
  rows: QuickViewRow[];
  /** CARDS the shown rows do not stand for → the overflow row's "{n} more tasks awaiting your input".
   *  Counted in CARDS, like the header: `shown cards + moreCount === waitingCount` holds by construction,
   *  so the card's arithmetic reads correctly against its own body. The tasks a question card stands for
   *  are NOT "more" — they are on screen, named in that card's own "Waiting on this answer" list. */
  moreCount: number;
  /** Every CARD in the window — one per open question, plus one per waiting task that contributes no
   *  groupable question. The number the pill states and the card's own body accounts for. One question
   *  blocking three tasks counts ONCE: it is one thing the reader has to deal with, and counting three
   *  put a "3" over a card showing the same question three times. See the note on `moreCount`.
   *  Plus the OUTREACH-ONLY questions (phase 5): one card each, counted from the server's total when it
   *  capped the list — see `buildIslandQuickView`. */
  waitingCount: number;
  /** Every action-required TASK in the window — the same population `waitingCount` collapses. NOT what the
   *  pill shows; it is what BI's `waiting_count` keeps meaning (a 1:1 shared contract with the phones and
   *  the widgets, which would be rewritten under their own feet by a redefinition) and what the tab counts
   *  under the same filter. `waitingTaskCount >= waitingCount − waitingOutreachCount`, always. */
  waitingTaskCount: number;
  /** How many of `waitingCount`'s cards are OUTREACH-ONLY questions (phase 5). The pill's BI
   *  `waiting_questions` reports `waitingCount − waitingOutreachCount` — task-borne cards only — the same
   *  number the phones report off the aggregate's `waitingQuestionsTotal`. */
  waitingOutreachCount: number;
  /** The TASK-BORNE cards inside `waitingCount` — what the pill's BI reports as `waiting_questions` (the phones read
   *  the aggregate's `waitingQuestionsTotal`). `waitingCount` adds the outreach-only questions and the open asks. */
  waitingQuestionCards: number;
  /**
   * The FULL windowed task list in reading order. The agent-started announcement names its agent from it,
   * and the blue card's working rows are selected out of it.
   */
  sorted: QuickViewTask[];
  /** The blue "agents at work" card: the working rows (live runs only), their overflow, and the counts. */
  working: WorkingRows;
  /**
   * The blue pill's leading glyphs — the DISTINCT working agents in activity (first-seen) order, one id
   * per glyph. `''` is the Maestro / agent-less bucket (a missing `agentId`, or a maestro action folded
   * into it). Mirrors `working.workingAgentIds`, surfaced at the top level so the island hook reads both
   * id lists from one place; `workingAgentIds.length === working.workingAgents`. Web derives this
   * client-side; the phones read the same per-agent order off the server aggregate.
   */
  workingAgentIds: string[];
  /**
   * The orange pill's leading glyphs — the DISTINCT action-required agents in the Tasks list's reading
   * order, one id per glyph. `''` is the Maestro / agent-less bucket. Taken over the same `actionable`
   * slice the orange card lists, so the pill's glyphs and the card's rows describe the same agents.
   */
  waitingAgentIds: string[];
}

/**
 * The peek size — how many rows EITHER card shows before its overflow row, and deliberately ONE constant
 * for both tones.
 *
 * It used to be two: 6 for the orange card, 3 for the blue one. Two numbers for one visual cap is how they
 * drifted apart in the first place, and six was the wrong one — because the orange rows FOLD (a routine's
 * runs, a goal's plan collapse behind their newest), six rows routinely stood for ten or more tasks. The
 * card stopped being a peek and became a second task list, directly above the "All tasks" button that
 * opens the real one. Three rows still say who is waiting and on what; the remainder is already accounted
 * for, in TASKS, by the overflow row.
 */
export const QUICK_VIEW_SHOWN = 3;

/**
 * One "Maestro action" — the Maestro at work on something that is NOT a task: a delegated external job
 * (a Wixel render) or a live free-run agent loop with no task row behind it. Web reads these off
 * `/api/tasks`'s `maestroActions` sibling (the same two conversation reads the server aggregate performs);
 * the phones read them off the aggregate itself. Absent field on the wire ≡ empty — an old server simply
 * has no actions to claim.
 */
export interface MaestroActionRow {
  /** The machine kind — 'wixel', 'agent-run', … Unknown kinds still render, on the generic fallback. */
  kind: string;
  /** Short user-facing line derived from the brief / the conversation name. Empty means absent. */
  label?: string;
  /** ISO instant the job was accepted — the row's elapsed stamp, when present. */
  startedAt?: string;
  /** The task whose run dispatched the job. When that task is in the list the job renders AS the task's
   *  row (see `buildWorkingRows`); otherwise the action row opens it. Absent = the Maestro's own job. */
  taskId?: string;
  /** The agent that dispatched it — the row's avatar and name instead of the Maestro's. */
  agentId?: string;
}

/**
 * The action row's hero sentence: its own `label` when it has one, else a localized per-kind fallback —
 * NEVER an empty line. Returns the copy KEY for the fallback because localization is the render site's;
 * an unknown/kind-less entry lands on the generic "Working for you".
 */
export type MaestroActionSentence = { kind: 'label'; text: string } | { kind: 'fallback'; key: 'agentsIsland.action.wixel' | 'agentsIsland.action.working' };

export function maestroActionSentence(action: Pick<MaestroActionRow, 'kind' | 'label'>): MaestroActionSentence {
  const label = action.label?.trim();
  if (label) return { kind: 'label', text: label };
  return { kind: 'fallback', key: action.kind === 'wixel' ? 'agentsIsland.action.wixel' : 'agentsIsland.action.working' };
}

export interface WorkingRows {
  /** Maestro-action rows — rendered FIRST (before the task rows), sharing the `QUICK_VIEW_SHOWN` cap. */
  actionRows: MaestroActionRow[];
  /** One row per WORKING task (`runInFlight === true`), newest activity first, filling what the cap has
   *  left after the action rows. */
  rows: QuickViewTask[];
  /** Working things beyond the shown rows (actions AND tasks) → the overflow row's count. */
  moreCount: number;
  /** ALL working things in the loaded list — tasks with a live run PLUS the maestro actions. */
  workingCount: number;
  /** DISTINCT agents behind the working tasks (missing `agentId` folds into one Maestro bucket, and any
   *  maestro action folds into that SAME bucket) — the blue pill's own count. */
  workingAgents: number;
  /** Those same distinct agents as an ordered id list (activity DESC, `''` for the Maestro bucket) — one
   *  id per glyph on the blue pill. `workingAgentIds.length === workingAgents`. */
  workingAgentIds: string[];
}

/** `lastActivityAt` as a sortable number; a row that carries none sorts to the TAIL (epoch 0), exactly as
 *  both phones do — no `updatedDate` fallback, so all three surfaces break the tie the same way. */
function activityMs(task: QuickViewTask): number {
  if (task.lastActivityAt) {
    const ms = Date.parse(task.lastActivityAt);
    if (!Number.isNaN(ms)) return ms;
  }
  return 0;
}

/**
 * The blue card's rows: ONLY tasks whose run is verifiably in flight (`runInFlight === true` — absent is
 * "not computed" and must select nothing), sorted by `lastActivityAt` DESC so the most recently active
 * agent reads first, capped to three with an overflow count. No origin fold here: a working row is a live
 * loop, and two live runs of one routine are genuinely two agents' worth of work happening now.
 *
 * `actions` are in-flight non-task work. They render FIRST (the card's subject is who is working right now),
 * share the same three-row cap, count into `workingCount` and the overflow, and fold into their dispatching
 * agent's bucket, else the ONE Maestro bucket `workingAgents` already keeps for agent-less tasks.
 *
 * **A job a TASK dispatched is that task's work, shown as that task's row.** A task that hands a render to
 * Wixel parks its own loop for the render's length, so its run reads not-in-flight and only the job record
 * says anything is happening. Shown as an action, that read as the Maestro's mark over a list of asset
 * names, opening the main chat — the reader could tell neither what was working nor get to it (reported).
 * So a task named by an action's `taskId` is promoted into the working rows (its agent, its Space, its own
 * sentence, a tap that opens it) and the action is dropped — once, even when the task was working anyway.
 * An action whose task is not in the list, or is held on an open question, stays an action row.
 */
export function buildWorkingRows(tasks: QuickViewTask[], allActions: MaestroActionRow[] = []): WorkingRows {
  // A row held on an OPEN QUESTION is not working, however live its run says it is. The veto lives HERE,
  // at the filter, and not only in `workingCounts`: the rows, the count and the agent dots are all
  // derived from this one list, so vetoing downstream would leave the blue card listing a task its own
  // count had excluded — the "a count that disagrees with its own list" failure the bucket rule exists
  // to prevent. An agent that has asked something and is waiting for the answer is parked, not running.
  // No outreach guard here (the phones keep one on `SymphonyTask.isOutreach`): the island has no outreach ROW
  // shape any more (`toQuickViewTask`), and a job's `taskId` names a task, never an outreach id.
  const jobTaskIds = new Set(allActions.flatMap((action) => (action.taskId ? [action.taskId] : [])));
  const working = tasks.filter((task) => (task.runInFlight === true || jobTaskIds.has(task.id)) && !hasOpenDecision(task));
  const workingIds = new Set(working.map((task) => task.id));
  const actions = allActions.filter((action) => !(action.taskId && workingIds.has(action.taskId)));
  const sorted = [...working].sort((a, b) => activityMs(b) - activityMs(a));
  // `workingCounts` re-reads `runInFlight`, so a promoted task is handed over as the live run it stands for.
  const counts = workingCounts(
    sorted.map((task) => (task.runInFlight === true ? task : { ...task, runInFlight: true })),
    actions,
  );
  const actionRows = actions.slice(0, QUICK_VIEW_SHOWN);
  const rows = sorted.slice(0, Math.max(0, QUICK_VIEW_SHOWN - actionRows.length));
  // One id per distinct working agent, in the same activity order the count was taken. An action has no
  // task row of its own, so fold its worker — its agent, else the Maestro (`''`) — in explicitly, exactly
  // as `workingCounts` folds it into `workingAgents`.
  const workingAgentIds = orderedDistinctAgentIds(sorted);
  for (const action of actions) {
    const id = action.agentId || '';
    if (!workingAgentIds.includes(id)) workingAgentIds.push(id);
  }
  return {
    actionRows,
    rows,
    moreCount: Math.max(0, actions.length + sorted.length - actionRows.length - rows.length),
    workingAgentIds,
    ...counts,
  };
}

/**
 * The working row's hero sentence, by the shared fallback chain: the agent's own `currentAction` →
 * the first NOT-completed plan step title → the localized "Working on: {title}" fallback. Returns the
 * SOURCE rather than a final string because the last tier is the caller's to localize — and because the
 * row's secondary title line is OMITTED exactly when the sentence already is the title (`kind: 'title'`),
 * so the two decisions must come from one answer.
 */
export type WorkingSentence = { kind: 'action' | 'step'; text: string } | { kind: 'title' };

export function workingSentence(task: Pick<QuickViewTask, 'currentAction' | 'plan'>): WorkingSentence {
  const action = task.currentAction?.trim();
  if (action) return { kind: 'action', text: action };
  const step = (task.plan ?? []).find((s) => !s.completed && s.title.trim());
  if (step) return { kind: 'step', text: step.title.trim() };
  return { kind: 'title' };
}

/**
 * Is this task ASKING THE USER FOR SOMETHING — the orange pill's own population (`ACTION_NEEDED` /
 * `PAUSED`, via the one shared bucket map). The card's rows are exactly this predicate applied to the
 * account-wide list, so the rows and the header can only ever describe the same kind of thing.
 */
export function isActionRequired(task: QuickViewTask): boolean {
  return bucketOfTask(task) === 'waiting';
}

/**
 * The outreach-only cards, merged into the task cards BY RECENCY (contract `island.why` rule 2) — the shared
 * `interleaveByRecency`: the task cards keep their own order, an outreach card (newest raised first) slots in
 * front of a task card only when raised STRICTLY later than that card's first task's `updatedDate`, and one with
 * no `createdDate` goes after every task card.
 */
function interleaveOutreach(taskCards: QuickViewRow[], outreachCards: QuickViewRow[]): QuickViewRow[] {
  return interleaveByRecency(
    taskCards,
    (taskCard) => (taskCard.question?.tasks[0] ?? taskCard).updatedDate,
    outreachCards,
    (card) => card.updatedDate,
  ) as QuickViewRow[];
}

/** One outreach-only question as a card: a question with no task behind it (see `QuickViewRow.outreachOnly`).
 *  `updatedDate` carries the question's `createdDate` — the recency the interleave reads. */
export function outreachCard(question: IslandOutreachQuestion): QuickViewRow {
  return {
    id: '',
    title: '',
    status: 'ACTION_NEEDED',
    workspaceId: '',
    workspaceName: '',
    updatedDate: question.createdDate,
    runCount: 1,
    question: { decisionId: question.decisionId, prompt: question, tasks: [] },
    outreachOnly: true,
  };
}

export { outreachOnlyQuestions } from './action-needed-count';

/**
 * The orange card's FULL card list, before the cap, and what it counts — `buildIslandQuickView`'s body,
 * exported so the shared contract's `orderedCardIds` can be asserted on the pre-cap order. `actionable` is the
 * waiting slice of the Tasks list's own order (`vm.sorted.filter(isActionRequired)`).
 */
export function orangeCards(
  actionable: QuickViewTask[],
  outreachQuestions: IslandOutreachQuestion[] = [],
  outreachQuestionsTotal?: number,
  openAsks?: OpenAsksRead | null,
): { cards: QuickViewRow[]; waitingCount: number; outreachCount: number; askCount: number; questionCards: number } {
  const taskCards: QuickViewRow[] = groupWaitingCards(actionable).map((card) =>
    card.kind === 'question'
      ? // The card's own fields are the FIRST blocked task's: the agent dot, the Space, and the id a tap
        // opens and BI reports. `decisionPrompts` is left exactly as that task carries it (never stripped
        // — the repair reads these rows), and the render site answers `question.prompt` alone, so a task
        // holding two questions does not draw both of them on each of its two cards.
        { ...card.tasks[0], runCount: 1, question: { decisionId: card.decisionId, prompt: card.prompt, tasks: card.tasks } }
      : { ...card.task, runCount: 1 },
  );
  // The outreach-only questions (rule 1: dedupe against EVERY waiting task's prompts — the task card already
  // asks it), then merged into the task cards by recency (rule 2).
  const { questions: deduped, count: outreachCount } = outreachOnlyQuestions(actionable, outreachQuestions, outreachQuestionsTotal);
  const rows = interleaveOutreach(taskCards, deduped.map(outreachCard));
  // THE shared count (`lib/action-needed-count.ts`) — the same function the Tasks screen's Action needed badge
  // reads, open asks included. The asks are counted here but drawn on the Tasks screen, not on this card: they
  // fall into the card's overflow ("{n} more"), whose arithmetic is against this same number.
  const count = actionNeededCount(actionable, { questions: outreachQuestions, total: outreachQuestionsTotal }, openAsks);
  return { cards: rows, waitingCount: count.total, outreachCount, askCount: count.openAsks, questionCards: count.questionCards };
}

/**
 * The island quick-view.
 *
 * `outreachQuestions` / `outreachQuestionsTotal` are `/api/tasks`'s outreach-only open questions (outreach
 * visibility phase 5, island 3a; pinned by `contract.json` `island.cases`). Absent (an old server, a failed
 * read, a not-yet-loaded list) ≡ `[]` / `undefined`: no outreach cards and nothing added — today's card.
 *  1. DEDUPE — a question whose `decisionId` is on ANY waiting task's `decisionPrompts` is that task's card
 *     already, and is dropped (web's `/api/tasks` does not pre-dedupe; the phones' aggregate does).
 *  2. INTERLEAVE by recency — see `interleaveByRecency`.
 *  3. COUNT — `waitingCount = taskCards + max(deduped.length, (total ?? list.length) − dropped)`: the server
 *     may cap the list below its total, and the cards it did not send are still waiting on the reader. The
 *     invariant `shown + moreCount === waitingCount` holds. `waitingTaskCount` still counts TASKS only.
 */
export function buildIslandQuickView(
  tasks: QuickViewTask[],
  now: number = Date.now(),
  actions: MaestroActionRow[] = [],
  outreachQuestions: IslandOutreachQuestion[] = [],
  outreachQuestionsTotal?: number,
  openAsks?: OpenAsksRead | null,
): IslandQuickView {
  // The Tasks list's own order — NOT a local recency sort. See the header note. The FULL list, because
  // `sorted` also feeds the blue card (its working rows and the Maestro's own actions) and the
  // agent-started announcement.
  const sorted = sortedForTaskList(tasks, now);
  // The orange card's population, narrowed BEFORE the fold. Filtering first is what makes a folded row
  // honest here: `groupTaskOrigins` collapses CONSECUTIVE same-origin, same-outcome tasks, so on the
  // filtered list a fold can only ever stand for several ACTION-NEEDED runs of one routine or goal — "2
  // Action needed", never the "2 Done" deck that was showing under an "Action needed" header. A fold of
  // finished runs is not a smaller version of an action-required row; it is a different claim, and the
  // reader taps it expecting the thing the card promised.
  const actionable = sorted.filter(isActionRequired);
  // ONE QUESTION = ONE CARD. A goal-scoped decision blocks every task in the goal's plan and the read
  // folds it back onto each of them, so three "send the invitation to friend N" tasks rendered three
  // identical-looking cards asking the same question. The collapse happens HERE and only here — the
  // `actionable` array above stays ungrouped and prompt-bearing for everything else that reads it (the
  // buckets, the tab counts, `unanswerableCardIds`, the repair) — and nothing is re-sorted: a card sits
  // where its question FIRST appeared in the order the comparator already produced.
  //
  // NO origin fold on this card (redesign, Figma 17339-142828 / 17666-231716): a routine's runs are each
  // their own card with a small "Routine"/"Goal" origin word on the meta line. `runCount` stays 1 on every
  // card, including a question card standing for three tasks — the arithmetic below counts CARDS.
  const { cards: rows, waitingCount, outreachCount, askCount, questionCards } = orangeCards(actionable, outreachQuestions, outreachQuestionsTotal, openAsks);
  const shown = rows.slice(0, QUICK_VIEW_SHOWN);
  const shownCards = shown.length;
  // The orange pill's glyphs, over the SAME action-required slice its card lists (before the origin fold,
  // so a folded routine still contributes its agent once) — reading order preserved, deduped. An
  // outreach-only question is the Maestro's (`''`) — added once, only when one is counted, as the phones'
  // aggregate does.
  const waitingAgentIds = orderedDistinctAgentIds(actionable);
  if (outreachCount + askCount > 0 && !waitingAgentIds.includes('')) waitingAgentIds.push('');
  const working = buildWorkingRows(sorted, actions);
  return {
    rows: shown,
    // CARDS, not tasks — see `moreCount`. `shown cards + moreCount === waitingCount` is the invariant the
    // card's body is read against.
    moreCount: Math.max(0, waitingCount - shownCards),
    waitingCount,
    // The TASK total, kept beside it: BI's `waiting_count` and every task-shaped count downstream still
    // mean tasks, and deriving both from this one list is what stops the two from drifting.
    waitingTaskCount: actionable.length,
    waitingOutreachCount: outreachCount,
    waitingQuestionCards: questionCards,
    sorted,
    working,
    workingAgentIds: working.workingAgentIds,
    waitingAgentIds,
  };
}


/** The fields {@link toQuickViewTask} reads off a wire task. Structural on purpose: `lib/tasks.ts`
 *  reaches the app graph, and this module must stay importable from a plain unit test. */
export type WireTaskForQuickView = Partial<Omit<QuickViewTask, 'workspaceName' | 'plan'>> & {
  rowKind?: string;
  plan?: QuickViewTask['plan'];
};

/**
 * One task as the island's peek holds it — the ONLY projection from the wire row onto the view-model.
 *
 * It lives HERE, beside the rules that consume it, rather than inside `useIslandQuickView`, because a
 * field dropped in this mapping is invisible: every component test still passes (the component is fine),
 * every hook test still passes (the hook is fine), and the feature is simply dead. That happened — the
 * share notice reached `QuickViewTask`'s TYPE and the row's code, and the mapping never copied it, so
 * the island went on offering "Reply" on a file share. A pure function is one a test can call.
 *
 * `workspaceName` is resolved by the caller, which is the only thing here that needs the workspace list.
 */
export function toQuickViewTask(task: WireTaskForQuickView, workspaceName: string): QuickViewTask {
  return {
    id: task.id ?? '',
    title: (task.title ?? '').trim(),
    status: task.status ?? '',
    agentId: task.agentId,
    // Both empty on an outreach row (an Outreach belongs to no Space) — the row simply shows no Space.
    workspaceId: task.workspaceId ?? '',
    // Resolved by the caller, which owns the workspace list; an outreach has no Space, so empty.
    workspaceName,
    summary: task.summary?.trim() || undefined,
    updatedDate: task.updatedDate,
    // Carried so the peek folds consecutive tasks of one origin exactly where the Tasks screen does —
    // a routine's runs, or a goal's laid-out plan.
    pulseIdentityId: task.pulseIdentityId || undefined,
    goalId: task.goalId || undefined,
    // The two fields the derived "laid out and never started" rule reads (`isNotStartedTask`), so the
    // bubble cannot show "In progress" on a row the Tasks screen shows as Not started.
    notBefore: task.notBefore || undefined,
    agentConversationId: task.agentConversationId || undefined,
    // Tri-state, and the spread is what preserves it: only a boolean crosses, so an absent field on the
    // wire stays ABSENT on the view-model row — "not computed", never false. Same for `lastActivityAt`.
    // The open questions this row is blocked on, carried for the SAME reason the fold keys are: the
    // orange card's population is `bucketOfTask(task) === 'waiting'`, and that verdict now depends on
    // these. A view-model that dropped them would leave the bubble listing a different set of rows than
    // the Tasks screen it peeks at, which is the drift this projection exists to prevent. Absence-
    // preserving, like the two tri-states below it — never `[]`, never `false`.
    ...(task.decisionPrompts?.length ? { decisionPrompts: task.decisionPrompts } : {}),
    ...(task.decisionPromptsUnavailable ? { decisionPromptsUnavailable: true as const } : {}),
    ...(typeof task.runInFlight === 'boolean' ? { runInFlight: task.runInFlight } : {}),
    ...(typeof task.lastActivityAt === 'string' && task.lastActivityAt ? { lastActivityAt: task.lastActivityAt } : {}),
    // The working row's sentence chain: the agent's own status line (empty string means absent), then
    // the first unticked plan step, then the localized "Working on: {title}" at the render site.
    currentAction: task.currentAction?.trim() || undefined,
    plan: task.plan ?? [],
  };
}


/** An entry of the contact-book head, as {@link outreachContext} reads it. Structural, so this module stays
 *  importable from a plain unit test. */
export type OutreachContextEntry = StageEntry<StageThread & { subject?: string; createdAt?: string }> & {
  contact?: { personId?: string; displayName?: string };
  channels?: { value?: string }[];
  recipientId?: string;
};

/** Who and what a question raised ON an outreach thread is about — the island question card's context row. */
export interface OutreachContext {
  /** How the contact opens: by person, or (address-only) by recipient — `contactThreadPath`'s identity. */
  identity: { personId: string } | { recipientId: string };
  outreachId: string;
  /** The contact's display name (the shared fallback chain). */
  name: string;
  /** The thread's channel for the `{name} · {channel}` line — an empty one reads `app`, as `channelParam` does. */
  channel: string;
  /** The thread's title — its subject, else the date it opened. */
  title: string;
}

/**
 * The outreach behind a question card, joined against the contact-book head by the prompt's
 * `outreachId` (`Decision.outreach_id`). Undefined when the question was not raised on a thread, or when
 * the join misses (the head has not loaded, or the thread is not in it) — the card then renders as an
 * ordinary question. Pure; mirrored by iOS/Android `outreachContext`.
 *
 * It decorates BOTH kinds of question card the island shows: one a task named, and (phase 5) an
 * outreach-only card (`QuickViewRow.outreachOnly`), whose person header and "Open conversation" this is.
 * On a join miss an outreach-only card still renders and stays answerable — just with no header and no link.
 */
export function outreachContext(
  /** A `TaskDecisionPrompt`. Typed loosely so a common-lib build that predates `outreachId` still fits. */
  prompt: object | undefined,
  contacts: OutreachContextEntry[] | undefined,
  unnamed: string,
  locale?: string,
): OutreachContext | undefined {
  const outreachId = (prompt as { outreachId?: unknown } | undefined)?.outreachId;
  if (typeof outreachId !== 'string' || !outreachId) return undefined;
  const hit = threadByOutreachId(contacts, outreachId);
  if (!hit) return undefined;
  const personId = hit.entry.contact?.personId ?? '';
  const recipientId = hit.entry.recipientId ?? '';
  if (!personId && !recipientId) return undefined;
  return {
    identity: personId ? { personId } : { recipientId },
    outreachId,
    name: contactDisplayName(hit.entry.contact, hit.entry.channels, unnamed),
    // NOT the chip rule: this feeds the `outreach.personOnChannel` line ("{name} · {channel}"), which every
    // surface words through the stage contract's `channelParam` — and that still reads an empty channel as
    // `app` (contract.json). The phones' island passes the raw channel to `channelParam`; this keeps web's
    // island saying the same thing until that contract changes. The chip (`threadChannel`) draws nothing.
    channel: threadChannel(hit.thread) || 'app',
    title: threadTitle(hit.thread, locale),
  };
}
